import React, { useState } from 'react';

const PASOS = [
  {
    num: '1',
    icon: '📂',
    titulo: 'Crea tus categorías',
    desc: 'Una categoría es un lugar donde guardas dinero: tu billetera con efectivo, tu cuenta del banco, una tarjeta, un fondo de ahorro. Puedes tener tantas como quieras.',
    ejemplo: 'Ej: "Efectivo", "Banco BCI", "Ahorro viaje", "Tarjeta Falabella"',
    color: '#0891b2',
  },
  {
    num: '2',
    icon: '↕️',
    titulo: 'Registra entradas y salidas',
    desc: 'Cada vez que entra o sale dinero de una categoría, regístralo. Los movimientos se agrupan por mes, con el total de entradas, salidas y balance de cada mes.',
    ejemplo: 'Ej: Recibiste tu sueldo → Entrada $800.000 en "Banco BCI". Pagaste el supermercado → Salida $45.000 en "Efectivo"',
    color: '#059669',
  },
  {
    num: '3',
    icon: '📌',
    titulo: '¿Qué es un compromiso?',
    desc: 'Un compromiso es dinero que ya está "reservado" para un gasto que sabes que viene, aunque todavía no lo hayas pagado. Al crearlo, ese dinero se descuenta de tu disponible real automáticamente.',
    ejemplo: 'Ej: El arriendo del mes es $350.000. Aunque no lo hayas pagado aún, creas el compromiso y ese dinero queda reservado.',
    color: '#f59e0b',
    highlight: true,
  },
  {
    num: '4',
    icon: '✅',
    titulo: 'Abona a tus compromisos',
    desc: 'Cuando pagas parte o todo de un compromiso, registras un "abono". Por defecto también se registra la salida de dinero de esa categoría, en un solo paso. Cuando el compromiso queda en cero, se marca como pagado.',
    ejemplo: 'Ej: Pagaste $150.000 del odontólogo que era $300.000 → Abono $150.000. Quedan $150.000 pendientes y tu saldo baja $150.000.',
    color: '#7c3aed',
  },
  {
    num: '5',
    icon: '🎯',
    titulo: 'Ahorra con metas',
    desc: 'Al abonar a una meta eliges de qué categoría sale. El dinero sigue en esa cuenta, pero queda reservado: baja tu disponible, no tu saldo. Si retiras de la meta, vuelve a estar disponible.',
    ejemplo: 'Ej: Reservas $50.000 de "Banco BCI" para "Viaje". Tu banco sigue mostrando lo mismo, pero ya no cuentas ese dinero para gastar.',
    color: '#0C447C',
  },
  {
    num: '6',
    icon: '💡',
    titulo: 'El número que importa: Disponible real',
    desc: 'Es lo que REALMENTE puedes gastar sin problema: Saldo − Comprometido − Reservado en metas. Siempre mira este número antes de gastar.',
    ejemplo: 'Tienes $500.000 en el banco, $320.000 comprometidos y $50.000 en metas → Tu disponible real es $130.000.',
    color: '#dc2626',
    highlight: true,
  },
];

const SECCIONES = [
  { icon: '🏠', titulo: 'Gastos Fijos', desc: 'Tus pagos recurrentes. Al registrar una salida como "Gasto fijo", queda marcado como pagado. Los semanales, quincenales y anuales se muestran en su equivalente mensual.' },
  { icon: '💳', titulo: 'Mis Deudas', desc: 'Lo que debes. Cada pago puede descontarse de una categoría; si borras el pago, también se borra la salida de dinero.' },
  { icon: '🤝', titulo: 'Me Deben', desc: 'Plata que prestaste. Cada cobro puede entrar a una categoría y te avisamos cuando se acerca la fecha de pago.' },
  { icon: '🛒', titulo: 'Mis Compras', desc: 'Compras con su detalle de productos, agrupadas por mes, una lista de compras y una predicción de lo que comprarás la próxima semana.' },
  { icon: '🏷️', titulo: 'Mi Negocio', desc: 'Para lo que vendes: crea tus productos con SKU, costo y precio; registra compras de mercadería y ventas (efectivo o transferencia). Ves tu ganancia real por venta y por producto, cuánto dinero tienes en stock y qué te toca reponer.' },
  { icon: '🔮', titulo: 'Simulador y Reportes', desc: 'Simula un gasto antes de hacerlo y revisa el resumen de cada mes.' },
];

const PREGUNTAS = [
  {
    q: '¿Cuál es la diferencia entre una "salida de dinero" y un "compromiso"?',
    a: 'Una salida es dinero que YA gastaste (ya salió de tu cuenta). Un compromiso es dinero que AÚN NO has gastado pero que ya sabes que tienes que gastar. Los compromisos te avisan que ese dinero no está realmente disponible.',
  },
  {
    q: '¿Por qué mi disponible real puede ser negativo?',
    a: 'Porque tienes más compromisos pendientes y dinero reservado en metas que saldo en esa categoría. ¡Es una alerta importante!',
  },
  {
    q: '¿Qué hago cuando pago un compromiso?',
    a: 'Solo haz un "abono" dejando marcada la opción "Descontar el pago de la categoría". Así se anota el abono y la salida de dinero al mismo tiempo. Si desmarcas esa opción, tendrás que registrar la salida por separado.',
  },
  {
    q: 'Si borro un movimiento, ¿qué pasa con el pago de deuda o el cobro?',
    a: 'Se borran juntos: si eliminas la salida de un pago de deuda, el pago desaparece de Mis Deudas (y al revés). Lo mismo con cobros de préstamos y abonos. Las compras y ventas se mantienen, solo dejan de descontar o sumar dinero.',
  },
  {
    q: '¿Cómo calcula Mi Negocio la ganancia real?',
    a: 'Ganancia = (precio de venta − costo del producto) × cantidad − gastos extra (envío, comisión, empaque). El costo se guarda al momento de vender, así que si después la mercadería te sale más cara, tus ventas anteriores no cambian.',
  },
  {
    q: '¿Puedo preguntarle a Billuyo si puedo gastar algo?',
    a: 'Sí. Billuyo ve tus datos en tiempo real (categorías, metas, deudas, gastos fijos y negocio) y te dice si puedes permitirte un gasto según tu disponible real.',
  },
];

function HowToUse() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="howto-page">
      <div className="page-header">
        <div>
          <h2>Cómo usar En Cintura</h2>
          <p className="page-subtitle">Todo lo que necesitas saber para controlar tus finanzas</p>
        </div>
      </div>

      {/* Concepto principal */}
      <div className="howto-hero">
        <div className="howto-hero-icon">💰</div>
        <div>
          <h3>La idea principal</h3>
          <p>
            La mayoría de las apps de finanzas te muestran tu saldo — pero eso te puede engañar.
            <strong> En Cintura</strong> te muestra tu <strong>disponible real</strong>:
            lo que tienes menos lo que ya prometiste gastar y lo que apartaste para tus metas.
            Así sabes exactamente qué puedes gastar sin quedar en rojo.
          </p>
        </div>
      </div>

      {/* Pasos */}
      <h3 className="howto-section-title">Paso a paso</h3>
      <div className="howto-steps">
        {PASOS.map((paso) => (
          <div key={paso.num} className={`howto-step ${paso.highlight ? 'howto-step-highlight' : ''}`}>
            <div className="howto-step-num" style={{ background: paso.color }}>{paso.num}</div>
            <div className="howto-step-content">
              <div className="howto-step-title">
                <span className="howto-step-icon">{paso.icon}</span>
                <h4>{paso.titulo}</h4>
              </div>
              <p className="howto-step-desc">{paso.desc}</p>
              <div className="howto-step-ejemplo">{paso.ejemplo}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Otras secciones */}
      <h3 className="howto-section-title" style={{ marginTop: 36 }}>Las demás secciones</h3>
      <div className="howto-steps">
        {SECCIONES.map((s) => (
          <div key={s.titulo} className="howto-step">
            <div className="howto-step-num" style={{ background: 'var(--primary-xlight)', fontSize: '1.2rem' }}>{s.icon}</div>
            <div className="howto-step-content">
              <div className="howto-step-title"><h4>{s.titulo}</h4></div>
              <p className="howto-step-desc">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <h3 className="howto-section-title" style={{ marginTop: 36 }}>Preguntas frecuentes</h3>
      <div className="howto-faq">
        {PREGUNTAS.map((item, i) => (
          <div key={i} className="faq-item">
            <button
              className="faq-question"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <span>{item.q}</span>
              <span className="faq-chevron">{openFaq === i ? '▲' : '▼'}</span>
            </button>
            {openFaq === i && (
              <div className="faq-answer">{item.a}</div>
            )}
          </div>
        ))}
      </div>

      {/* Billuyo tip */}
      <div className="howto-billuyo-tip">
        <div className="howto-billuyo-avatar">✦</div>
        <div>
          <strong>Tip de Billuyo:</strong> Si en algún momento no entiendes algo o quieres saber si puedes hacer un gasto,
          abre el chat de Billuyo (botón marrón abajo a la derecha) y pregúntame directamente. Tengo acceso a tus números reales.
        </div>
      </div>
    </div>
  );
}

export default HowToUse;
